import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import { BasketItem } from 'store/basket/basketAPI';
import { CandidateProductDetails } from 'store/candidate/types';
import { PreviousCenterNumbersRequest } from 'store/candidate/apiTypes';
import { ROUTES, ExamType, mapServerError } from 'core';
import { mapToCandidateProductDetailsRequest } from 'store/candidate/candidateHelpers';
import { selectPreparationCentre, selectSelectedCountryId } from 'store/country/countrySlice';
import { setCandidateProductDetails } from 'store/candidate/candidateSlice';
import { updateBasketItems } from 'store/basket/basketSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  useValidateCandidateProductDetailsMutation,
  useValidateCentresNumbersMutation,
} from 'store/candidate/candidateAPI';

import { ServerError } from '@britishcouncil/react-forms';

export const useCandidateProductDetailsSubmit = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const countryId = useAppSelector(selectSelectedCountryId);
  const pcInfo = useAppSelector(selectPreparationCentre);
  const [validateCentresNumbers, validateCentresNumbersData] = useValidateCentresNumbersMutation();
  const [validateCandidateProductDetails, validateCandidateProductDetailsData] =
    useValidateCandidateProductDetailsMutation();

  const submit = (
    basketItems: BasketItem[],
    candidateProductDetails: CandidateProductDetails,
    candidateId?: number | null
  ) => {
    const exams = getExamsWithRetake(basketItems, candidateProductDetails);
    const awardingBodyId = exams.map((x) => x.awardingBodyId)[0];
    const previousCenterNumbers = getPreviousCenterNumbers(exams, awardingBodyId);

    validateCentresNumbers(previousCenterNumbers)
      .unwrap()
      .then(() => {
        const details = mapToCandidateProductDetailsRequest({
          countryId,
          candidateId,
          exams,
          candidateProductDetails,
          pcInfo,
        });
        validateCandidateProductDetails(details)
          .unwrap()
          .then(() => {
            dispatch(updateBasketItems(exams));
            dispatch(setCandidateProductDetails(candidateProductDetails));
            navigate(ROUTES.TERMS_AND_CONDITIONS);
          }).catch(rejected => console.error(rejected));
      }).catch(rejected => console.error(rejected))
  };

  const serverErrors = useMemo<ServerError[]>(
    () =>
      [validateCentresNumbersData, validateCandidateProductDetailsData]
        .map((x) => (x.isError ? mapServerError(x.error) : []))
        .flat(),
    [validateCentresNumbersData, validateCandidateProductDetailsData]
  );

  return {
    submit,
    serverErrors,
    isSubmitting: validateCentresNumbersData.isLoading || validateCandidateProductDetailsData.isLoading,
  };
};

//HELPERS
function getExamsWithRetake(basketItems: BasketItem[], candidateProductDetails: CandidateProductDetails) {
  return basketItems.map((item, i) => ({ ...item, retake: candidateProductDetails.examRetake[i] ?? null }));
}

function getPreviousCenterNumbers(basketItems: BasketItem[], awardingBodyId: number) {
  return basketItems.reduce(
    (prev, next) => {
      if (next.retake && next.retake?.type !== ExamType.Standard) {
        prev.previousCandidateNumber = next.retake.previousCandidateNumber || null;
        prev.centresNumbers.push({
          examOptionId: next.examOptionId,
          centerNumber: next.retake?.previousCenterNumber,
          awardingBodyId: awardingBodyId,
          candidateNumber: next.retake?.previousCandidateNumber,
        });
      }
      return prev;
    },
    { previousCandidateNumber: null, centresNumbers: [] } as PreviousCenterNumbersRequest
  );
}
